/**
 * Copy-paste system instructions for LLMs building Promang checklist type JSON.
 * English: best compatibility with models; technical keys match the app.
 */
export const PROMANG_CHECKLIST_TYPE_AI_GUIDE = `You generate **Promang checklist type JSON** — reusable checklist templates (HMS / quality) used in the Checklist Type editor.

## Output rules
- Return **one** JSON object only: \`{ "title": "...", "description": "...", "default_response_actions": [...], "items": [ ... ] }\`.
- \`items\` may also be returned alone as a raw array \`[ ... ]\` if metadata is omitted.
- No markdown fences, no commentary before or after the JSON.
- Omit database \`id\` on **new** points. If the JSON already has item ids (export of an existing type), keep them so save updates in place.

## Top-level fields

| Field | Type | Description |
|-------|------|-------------|
| \`title\` | string | Checklist type name |
| \`description\` | string | Short introduction shown when executing a checklist |
| \`default_response_actions\` | string[] | Default response buttons for all points |
| \`items\` | array | Checklist points in order |

### default_response_actions / response_actions

Allowed values:
- \`performed\` — marked as done
- \`approved\` — formally approved
- \`na\` — not applicable
- \`deviation\` — deviation / non-conformance

Default when omitted: \`["approved", "deviation"]\`.

Per-point \`response_actions\`: use \`null\` or omit to inherit from \`default_response_actions\`.

## Item schema

Each element in \`items\`:

\`\`\`json
{
  "title": "Point title (required)",
  "guidance": "Read-only instructions for what to verify",
  "documents_required": false,
  "response_actions": null,
  "input_fields": []
}
\`\`\`

- \`title\` — required; short name of the check point
- \`guidance\` — customer-facing guidance (shown read-only during execution)
- \`description\` — legacy alias for \`guidance\`; prefer \`guidance\`
- \`documents_required\` — when \`true\`, photos/documents are expected for this point
- \`response_actions\` — optional override; \`null\` = inherit type defaults
- \`input_fields\` — optional custom fields filled during execution

### input_fields

\`\`\`json
{
  "label": "Temperature",
  "input_type": "number",
  "required": true,
  "unit": "°C",
  "placeholder": "e.g. 20"
}
\`\`\`

- \`input_type\`: \`text\`, \`number\`, or \`textarea\`
- Do **not** include \`key\`; the app derives keys from labels

## Design tips
- Write concrete, operational check points — not generic "Item 1".
- Add \`guidance\` when the check is not obvious from the title alone.
- Use \`input_fields\` for measurements, readings, or free-text observations.
- Set \`documents_required: true\` when photo evidence is natural (damage, labels, signage).
- Use clear Norwegian (or requested language) unless told otherwise.

## Example (minimal)

\`\`\`json
{
  "title": "Weekly safety walk",
  "description": "Standard HSE inspection before work starts",
  "default_response_actions": ["performed", "approved", "na", "deviation"],
  "items": [
    {
      "title": "Access and barriers",
      "guidance": "Verify access routes are clear and hazardous zones are marked.",
      "documents_required": false
    },
    {
      "title": "Air quality reading",
      "guidance": "Measure and record air quality where required.",
      "documents_required": true,
      "input_fields": [
        { "label": "CO₂ (ppm)", "input_type": "number", "required": true, "unit": "ppm" }
      ]
    }
  ]
}
\`\`\`
`;
